/**
 * Path Profile overlay for the Canvas 2D (OpenLayers) engine.
 *
 * Mirrors the MapLibre GeoJSON overlay the Raster ToolCard draws while the
 * operator picks a path: the route line, one marker per clicked vertex, and a
 * highlight for the point currently hovered on the profile graph.
 */

import Feature from 'ol/Feature.js';
import LineString from 'ol/geom/LineString.js';
import Point from 'ol/geom/Point.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import CircleStyle from 'ol/style/Circle.js';
import Fill from 'ol/style/Fill.js';
import Stroke from 'ol/style/Stroke.js';
import Style from 'ol/style/Style.js';
import { fromLonLat } from 'ol/proj.js';
import type { FeatureLike } from 'ol/Feature.js';

import { tokens } from '@/design';

/** Kept above every data layer so the route never hides under raster tiles. */
const OVERLAY_Z_INDEX = 9000;

export type PathProfileVertex = readonly [number, number];

function styleForRole(role: unknown): Style {
  if (role === 'cursor') {
    return new Style({
      image: new CircleStyle({
        radius: 7,
        fill: new Fill({ color: tokens.color.accent.primary }),
        stroke: new Stroke({ color: tokens.color.ink.inverse, width: 2 }),
      }),
    });
  }
  if (role === 'vertex') {
    return new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: tokens.color.semantic.warning }),
        stroke: new Stroke({ color: tokens.color.ink.inverse, width: 2 }),
      }),
    });
  }
  return new Style({
    stroke: new Stroke({ color: tokens.color.semantic.warning, width: 3 }),
  });
}

export function createPathProfileOverlayLayer(): VectorLayer {
  return new VectorLayer({
    source: new VectorSource(),
    style: (feature: FeatureLike) => styleForRole(feature.get('role')),
    zIndex: OVERLAY_Z_INDEX,
    updateWhileAnimating: false,
    updateWhileInteracting: false,
    properties: { geosoftId: '__path-profile__' },
  });
}

export function pathProfileOverlayFeatures(
  vertices: readonly PathProfileVertex[],
  cursor: PathProfileVertex | null,
): Feature[] {
  const features: Feature[] = [];
  if (vertices.length >= 2) {
    features.push(
      new Feature({
        geometry: new LineString(vertices.map((vertex) => fromLonLat([...vertex]))),
        role: 'path',
      }),
    );
  }
  for (const vertex of vertices) {
    features.push(
      new Feature({
        geometry: new Point(fromLonLat([...vertex])),
        role: 'vertex',
      }),
    );
  }
  if (cursor) {
    features.push(
      new Feature({
        geometry: new Point(fromLonLat([...cursor])),
        role: 'cursor',
      }),
    );
  }
  return features;
}
